import cssStyle from './styles/App.module.scss';
import type { MutableRefObject, PropsWithChildren, ReactElement } from 'react';
import React, { useEffect, useRef, useState } from 'react';

type MarqueeProps = PropsWithChildren<{ determinantRef: MutableRefObject<HTMLElement>, addToManager?: (elemWidth: number) => void, maxWidth?: number, resetManager?: () => void, recalculate?: any }>

export default function Marquee({ children, determinantRef, addToManager, maxWidth, resetManager, recalculate }: MarqueeProps): ReactElement {
    const [needsMarquee, setNeedsMarquee] = useState<boolean>(false);
    const [needsCalculation, setNeedsCalculation] = useState<boolean>(true);
    const [relativeHeight, setRelativeHeight] = useState<number>(0);
    const [determinantStyle, setDeterminantStyle] = useState<CSSStyleDeclaration>(null);
    const [widthState, setWidthState] = useState<string>(maxWidth + 'px');
    const childRef = useRef<HTMLDivElement>(null);
    const prefersReducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    useEffect(() => {
        if (needsCalculation) {
            if (childRef.current) {
                childRef.current.parentElement.classList.add(cssStyle.parent);
            }
            if (childRef.current && determinantRef.current) {
                determinantRef.current.classList.add(cssStyle.sizeDeterminant);
                const childSize = childRef.current.getBoundingClientRect();
                const determinantSize = determinantRef.current.getBoundingClientRect();
                const willNeedMarquee = Math.floor(childSize.x) < Math.floor(determinantSize.x) || (Math.floor(childSize.width) >= Math.floor(determinantSize.width) && childSize.width > 0 && determinantSize.width > 0);
                if (determinantSize.height > 0 && determinantStyle && determinantStyle.display !== 'grid' && willNeedMarquee) {
                    determinantRef.current.style.height = determinantSize.height + 'px';
                } else {
                    determinantRef.current.style.height = null;
                }
                if (childSize.height > 0) {
                    setRelativeHeight(childSize.height);
                }
                setNeedsMarquee(willNeedMarquee);
                if (addToManager) addToManager(childSize.width);
                if (!willNeedMarquee) determinantRef.current.classList.remove(cssStyle.sizeDeterminant);
                setNeedsCalculation(false);
            }
        }
    }, [
        childRef,
        determinantRef,
        needsCalculation,
        prefersReducedMotion
    ]);
    
    useEffect(() => {
        setWidthState(prefersReducedMotion ? '100%' : maxWidth + 'px');
    }, [prefersReducedMotion, maxWidth]);
    
    
    useEffect(() => {
        if (!needsCalculation) {
            setNeedsMarquee(false);
            setNeedsCalculation(true);
            if (resetManager) resetManager();
        }
    }, [recalculate]);
    
    useEffect(() => {
        const style = getComputedStyle(determinantRef.current);
        setDeterminantStyle(style);
        if (!['fixed', 'absolute', 'relative'].includes(style.position)) {
            determinantRef.current.classList.add(cssStyle.relative);
        }
    }, []);
    
    const titles: Array<string> = [];
    React.Children.map(children, (child) => {
        if (!child) return;
        const img = child as ReactElement<HTMLImageElement>;
        const html = child as ReactElement<HTMLElement>;
        if (img?.props?.title || img?.props?.alt) {
            titles.push(img.props.title || img.props.alt);
        } else if (html?.props?.title) {
            titles.push(html.props.title);
        } else {
            titles.push(child.toString());
        }
    });
    
    if (needsMarquee) {
        return (<div className={cssStyle.container} style={{ height: relativeHeight + 'px' }} title={titles.join(' - ')}>
            <div className={cssStyle.flexer}>
                <div className={cssStyle.mainChild} style={{ width: widthState }}>
                    {children}
                </div>
                <div className={cssStyle.copyChild} style={{ width: widthState }}>
                    {children}
                </div>
            </div>
        </div>);
    }
    return <div className={cssStyle.unobtrusiveDiv} ref={childRef}>{ children }</div>;
}